// --- Transform helpers (column-major 4x4 arrays) ---
let move = (tx, ty, tz) => [
   1,0,0,0,
   0,1,0,0,
   0,0,1,0,
   tx,ty,tz,1
];

let scale = (sx, sy, sz) => {
   if (sy === undefined) { sy = sx; sz = sx; }
   return [
      sx,0,0,0,
      0,sy,0,0,
      0,0,sz,0,
      0,0,0,1
   ];
};

let turnX = a => {
   let c = Math.cos(a), s = Math.sin(a);
   return [
      1,0,0,0,
      0,c,s,0,
      0,-s,c,0,
      0,0,0,1
   ];
};

let turnY = a => {
   let c = Math.cos(a), s = Math.sin(a);
   return [
      c,0,-s,0,
      0,1,0,0,
      s,0,c,0,
      0,0,0,1
   ];
};

let turnZ = a => {
   let c = Math.cos(a), s = Math.sin(a);
   return [
      c,s,0,0,
      -s,c,0,0,
      0,0,1,0,
      0,0,0,1
   ];
};

// --- Quadric coefficient matrices (as 4x4 column-major arrays) ---
// Sphere: x*x + y*y + z*z - 1 <= 0
let sphere = [
   1,0,0,0,
   0,1,0,0,
   0,0,1,0,
   0,0,0,-1
];

// Paraboloids:
// ParabX: x + y*y + z*z <= 0  (linear x term)
let parabX = [
   0,0,0,0.5,
   0,1,0,0,
   0,0,1,0,
   0.5,0,0,0
];

// ParabY: x*x + y + z*z <= 0  (linear y term)
let parabY = [
   1,0,0,0,
   0,0,0,0.5,
   0,0,1,0,
   0,0.5,0,0
];

// ParabZ: x*x + y*y + z <= 0  (linear z term)
let parabZ = [
   1,0,0,0,
   0,1,0,0,
   0,0,0,0.5,
   0,0,0.5,0
];

// Slabs: x*x - 1 <= 0 etc.
let slabX = [
   1,0,0,0,
   0,0,0,0,
   0,0,0,0,
   0,0,0,-1
];
let slabY = [
   0,0,0,0,
   0,1,0,0,
   0,0,0,0,
   0,0,0,-1
];
let slabZ = [
   0,0,0,0,
   0,0,0,0,
   0,0,1,0,
   0,0,0,-1
];

// Tubes: Tube X: y*y + z*z - 1 <= 0, etc.
let tubeX = [
   0,0,0,0,
   0,1,0,0,
   0,0,1,0,
   0,0,0,-1
];
let tubeY = [
   1,0,0,0,
   0,0,0,0,
   0,0,1,0,
   0,0,0,-1
];
let tubeZ = [
   1,0,0,0,
   0,1,0,0,
   0,0,0,0,
   0,0,0,-1
];

// let cone = [
//   1, 0, 0, 0,  // x^2 coefficient
//   0, -1, 0, 0, // -y^2
//   0, 0, 1, 0,  // z^2
//   0, 0, 0, 0   // no translation term
// ]

// let cone = [
//   1, 0, 0, 0,
//   0, -1, 0, 2,  // y^2 - 2*y
//   0, 0, 1, 0,
//   0, 0, 0, -1   // constant
// ];


// CONE PARAMETERS
const coneSlimness = 10;      // Higher = slimmer cone (1 = normal, 4 = slim, 9 = very slim)
const coneApexY = 2;          // Y position of cone apex
const coneBottomY = -1;       // Y position of bottom cap
const coneTopCutY = -0.5;      // Y position where top is cut (lower = bigger flat top)

// Cone: coneSlimness*(x² + z²) = (y - coneApexY)²
let cone = [
  coneSlimness, 0, 0, 0,
  0, -1, 0, 2 * coneApexY,
  0, 0, coneSlimness, 0,
  0, 0, 0, -coneApexY*coneApexY
];

// Bottom slab: y² - 1 <= 0  (keeps -1 <= y <= 1)
let slabYBottom = [
   0, 0, 0, 0,
   0, 1, 0, 0,
   0, 0, 0, 0,
   0, 0, 0, coneBottomY
];

// Top cut: y - coneTopCutY <= 0  (keeps y <= coneTopCutY)
let slabYTop = [
   0, 0, 0, 0,
   0, 1, 0, 0,
   0, 0, 0, 0,
   0, 0, 0, coneTopCutY
];


// All space: -1 <= 0
let space = [
   0,0,0,0,
   0,0,0,0,
   0,0,0,0,
   0,0,0,-1
];

// Export transforms and quadric matrices so other modules can import them
export {
  move,
  scale,
  turnX,
  turnY,
  turnZ,
  sphere,
  parabX,
  parabY,
  parabZ,
  slabX,
  slabY,
  slabZ,
  tubeX,
  tubeY,
  tubeZ,
  space,
  cone,
  slabYTop,slabYBottom
};






// Module-level GL fallback (kept for backward compatibility) and current canvas tracker
let gl = null;
let currentCanvas = null; // the canvas whose scene.update() is executing

// exported helper kept for compatibility: setUniform('1f', 'u_time', value)
export function setUniform(type, name, a, b, c) {
  try {
    // prefer the GL context for the currently-rendering canvas (so multiple canvases don't clash)
    const targetCanvas = currentCanvas || null;
    const targetGL = targetCanvas && targetCanvas._gl ? targetCanvas._gl : gl;
    if (!targetGL || !targetGL.program) return;
    const loc = targetGL.getUniformLocation(targetGL.program, name);
    if (!loc) {
      // uniform not found (maybe optimized away)
      return;
    }
    // ensure program is in use
    targetGL.useProgram(targetGL.program);
    const fn = targetGL['uniform' + type];
    if (typeof fn !== 'function') {
      console.warn('webgl0: unknown uniform type', type, 'for', name);
      return;
    }
    // call with the correct GL context
    fn.call(targetGL, loc, a, b, c);
  } catch (e) {
    console.warn('webgl0: setUniform failed', e);
  }
};

export function gl_start(canvas, scene) {
  if (!canvas) return null;

  // prefer opaque context so we don't accidentally composite with page background
  const ctx = canvas.getContext('webgl2', { alpha: false }) || canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
  if (!ctx) {
    console.error('webgl0: WebGL not supported');
    return null;
  }

  // store per-canvas GL on the canvas element
  canvas._gl = ctx;
  // also maintain module-level gl for backwards compatibility (single-canvas apps)
  gl = ctx;

  const glLocal = ctx;
  glLocal.enable(glLocal.BLEND);
  glLocal.blendFunc(glLocal.SRC_ALPHA, glLocal.ONE_MINUS_SRC_ALPHA);
  glLocal.clearColor(0.0, 0.0, 0.0, 1.0);

  canvas.setShaders = function(vertexShader, fragmentShader) {
    // delete old program if present
    try { if (glLocal.program) { glLocal.deleteProgram(glLocal.program); glLocal.program = null; } } catch (e) {}

    const program = glLocal.createProgram();

    function addShaderSafe(type, src) {
      const shader = glLocal.createShader(type);
      glLocal.shaderSource(shader, src);
      glLocal.compileShader(shader);
      if (!glLocal.getShaderParameter(shader, glLocal.COMPILE_STATUS)) {
        const msg = glLocal.getShaderInfoLog(shader);
        console.error('webgl0: shader compile error', msg);
        glLocal.deleteShader(shader);
        return false;
      }
      glLocal.attachShader(program, shader);
      return true;
    }

    const okV = addShaderSafe(glLocal.VERTEX_SHADER, vertexShader);
    const okF = addShaderSafe(glLocal.FRAGMENT_SHADER, fragmentShader);
    if (!okV || !okF) {
      try { glLocal.deleteProgram(program); } catch (e) {}
      return false;
    }

    glLocal.linkProgram(program);
    if (!glLocal.getProgramParameter(program, glLocal.LINK_STATUS)) {
      console.error('webgl0: program link error', glLocal.getProgramInfoLog(program));
      try { glLocal.deleteProgram(program); } catch (e) {}
      return false;
    }

    glLocal.program = program;
    glLocal.useProgram(program);

    // full-screen two-triangles buffer (6 vertices)
    const buf = glLocal.createBuffer();
    glLocal.bindBuffer(glLocal.ARRAY_BUFFER, buf);
    glLocal.bufferData(glLocal.ARRAY_BUFFER, new Float32Array([ -1, 1, 0,   1, 1, 0,   -1, -1, 0,   1, -1, 0,   -1, -1, 0,   1, 1, 0 ]), glLocal.STATIC_DRAW);
    const aPos = glLocal.getAttribLocation(program, 'aPos');
    if (aPos >= 0) {
      glLocal.enableVertexAttribArray(aPos);
      glLocal.vertexAttribPointer(aPos, 3, glLocal.FLOAT, false, 0, 0);
    }

    return true;
  };

  // try to set user shader; fall back to a magenta shader if user's shader fails
  let ok = canvas.setShaders(scene.vertexShader, scene.fragmentShader);
  if (!ok) {
    console.warn('webgl0: scene shader failed — using fallback magenta shader');
    const fbVert = `#version 300 es\nin vec3 aPos;void main(){gl_Position=vec4(aPos,1.0);}`;
    const fbFrag = `#version 300 es\nprecision highp float;out vec4 fragColor;void main(){fragColor = vec4(1.0,0.0,1.0,1.0);}`;
    ok = canvas.setShaders(fbVert, fbFrag);
    if (!ok) { console.error('webgl0: fallback shader failed'); canvas.stopRender = () => {}; return glLocal; }
  }

  // Resize helper (DPR-aware)
  const resizeToDisplaySize = () => {
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    const w = Math.max(1, Math.round(rect.width * dpr));
    const h = Math.max(1, Math.round(rect.height * dpr));
    let changed = false;
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w; canvas.height = h;
      canvas.style.width = rect.width + 'px';
      canvas.style.height = rect.height + 'px';
      glLocal.viewport(0, 0, canvas.width, canvas.height);
      changed = true;
    }
    return changed;
  };

  // observe size changes and also run once to initialize
  let ro = null;
  try { ro = new ResizeObserver(resizeToDisplaySize); ro.observe(canvas); } catch (e) { /* ignore */ }
  resizeToDisplaySize();

  // main RAF render loop — updates u_time and u_resolution each frame
  let startTime = performance.now();
  let rafId = null;
  function render(now) {
    resizeToDisplaySize();

    // Set currentCanvas so setUniform inside scene.update() targets this canvas's GL
    currentCanvas = canvas;
    try { if (scene.update) scene.update(); } catch (e) { console.error('webgl0: scene.update error', e); }
    currentCanvas = null;

    // update common uniforms if present (also useful if scene.update didn't set them)
    if (glLocal && glLocal.program) {
      glLocal.useProgram(glLocal.program);
      const tLoc = glLocal.getUniformLocation(glLocal.program, 'u_time');
      if (tLoc) glLocal.uniform1f(tLoc, (now - startTime) / 1000);
      const rLoc = glLocal.getUniformLocation(glLocal.program, 'u_resolution');
      if (rLoc) glLocal.uniform2f(rLoc, canvas.width, canvas.height);
    }

    glLocal.clear(glLocal.COLOR_BUFFER_BIT);
    try { glLocal.drawArrays(glLocal.TRIANGLES, 0, 6); } catch (e) { /* ignore draw errors */ }
    rafId = requestAnimationFrame(render);
  }
  rafId = requestAnimationFrame(render);

  // stopRender cleans up RAF, program, and observer
  canvas.stopRender = () => {
    try { if (rafId) cancelAnimationFrame(rafId); } catch (e) {}
    try { if (ro) { ro.disconnect(); ro = null; } } catch (e) {}
    try {
      if (glLocal && glLocal.program) {
        const p = glLocal.program;
        glLocal.useProgram(null);
        try { glLocal.deleteProgram(p); } catch (e) {}
        glLocal.program = null;
      }
    } catch (e) {}
  };

  return glLocal;
}


// Helper to register HMR for a scene: pass the current module (module) and a factory that creates a new Scene.
export function registerSceneHMR(moduleRef, canvas, createScene) {
  if (!moduleRef || !moduleRef.hot) return;
  try {
    moduleRef.hot.accept(() => {
      try { if (canvas && typeof canvas.stopRender === 'function') canvas.stopRender(); } catch (e) {}
      try {
        const s = createScene();
        gl_start(canvas, s);
      } catch (e) {
        console.error('webgl0: Error re-initializing scene after HMR:', e);
      }
    });
  } catch (e) {
    // ignore
  }
}